import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

// Howyin palette
const INK = "#1A1614";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const HAIR = "#E8E1D3";
const MUTED = "#8A857F";

const TYPE_META = {
  spin: { label: "🎡 Spin", bg: "#FDF6DF", fg: "#8A6D00" },
  dice: { label: "🎲 Dice", bg: "#EAF2FF", fg: "#2563EB" },
  card: { label: "🃏 Card", bg: "#F3EAFB", fg: "#7C3AED" },
  lottery: { label: "🎟️ Lottery", bg: "#FDEAEA", fg: "#DC2626" },
  trivia: { label: "🧠 Trivia", bg: "#E8F6EC", fg: "#15803D" },
};

const typeBadge = (t) => {
  const c = TYPE_META[t] || { label: t, bg: "#F1EEE7", fg: MUTED };
  return (
    <span style={{ background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap" }}>
      {c.label}
    </span>
  );
};

const StatTile = ({ label, value }) => (
  <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, padding: "14px 18px", minWidth: 150 }}>
    <div style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 22, color: INK, fontWeight: 700, marginTop: 2 }}>{value}</div>
  </div>
);

const resultText = (r) => {
  if (r.status === "cancelled") return "Cancelled (refunded)";
  const winners = (r.result && r.result.winnerIds) || [];
  if (r.type === "spin" || r.type === "dice" || r.type === "card") {
    const won = (r.entries && r.entries[0] && r.entries[0].wonAmount) || 0;
    return won > 0 ? `Won ${won}` : "House won";
  }
  if (r.type === "lottery") return winners.length ? "Winner drawn" : "No tickets";
  if (r.type === "trivia") {
    if (!r.entries || !r.entries.length) return "No players";
    return winners.length ? `${winners.length} winner${winners.length > 1 ? "s" : ""}` : "Nobody right (refunded)";
  }
  return "-";
};

const LiveGamesLogs = () => {
  const [rounds, setRounds] = useState([]);
  const [stats, setStats] = useState({ platformCut: 0, pot: 0, rounds: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchRounds = useCallback(async () => {
    setLoading(true);
    try {
      const q = `liveGame/adminRounds?page=${page}&limit=${limit}` + (type ? `&type=${type}` : "");
      const res = await apiInstanceFetch.get(q);
      if (res && res.status) {
        setRounds(res.rounds || []);
        setTotal(res.total || 0);
        setStats(res.stats || { platformCut: 0, pot: 0, rounds: 0 });
      }
    } catch (e) {
      console.error("liveGame adminRounds fetch", e);
    } finally {
      setLoading(false);
    }
  }, [page, limit, type]);

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const th = { textAlign: "left", padding: "10px 12px", fontSize: 12, color: MUTED, fontWeight: 600, borderBottom: `1px solid ${HAIR}`, whiteSpace: "nowrap" };
  const td = { padding: "10px 12px", fontSize: 13, color: INK, borderBottom: `1px solid ${HAIR}`, verticalAlign: "top" };

  const hostName = (r) => {
    const h = r.hostUserId;
    if (h && (h.firstName || h.lastName)) return `${h.firstName || ""} ${h.lastName || ""}`.trim();
    return r.type === "spin" || r.type === "dice" || r.type === "card"
      ? (r.entries && r.entries[0] && r.entries[0].name) || "-"
      : "-";
  };

  return (
    <div style={{ padding: 16 }}>
      <Title name="Live Games" />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "12px 0" }}>
        <StatTile label="Platform revenue (coins)" value={stats.platformCut || 0} />
        <StatTile label="Total staked (coins)" value={stats.pot || 0} />
        <StatTile label="Rounds settled" value={stats.rounds || 0} />
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "12px 0" }}>
        <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}
          style={{ padding: "9px 12px", borderRadius: 10, border: `1px solid ${HAIR}`, fontSize: 13, background: CREAM }}>
          <option value="">All games</option>
          <option value="spin">Spin</option>
          <option value="dice">Dice</option>
          <option value="card">Card</option>
          <option value="lottery">Lottery</option>
          <option value="trivia">Trivia</option>
        </select>
        <span style={{ fontSize: 12, color: MUTED }}>{total} rounds</span>
      </div>

      <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr>
              <th style={th}>Date / Time</th>
              <th style={th}>Game</th>
              <th style={th}>Host / Player</th>
              <th style={th}>Players</th>
              <th style={th}>Pot</th>
              <th style={th}>Platform cut</th>
              <th style={th}>Host share</th>
              <th style={th}>Result</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={8}>Loading…</td></tr>
            ) : rounds.length === 0 ? (
              <tr><td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={8}>No game rounds yet.</td></tr>
            ) : (
              rounds.map((r) => (
                <tr key={r._id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{dayjs(r.createdAt).format("DD MMM YYYY")}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>{dayjs(r.createdAt).format("hh:mm:ss A")}</div>
                  </td>
                  <td style={td}>{typeBadge(r.type)}</td>
                  <td style={td}>{hostName(r)}</td>
                  <td style={td}>{(r.entries || []).length}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{r.pot || 0}</td>
                  <td style={{ ...td, fontWeight: 700, color: (r.platformCut || 0) >= 0 ? GOLD : "#DC2626" }}>{r.platformCut || 0}</td>
                  <td style={td}>{r.hostShare || 0}</td>
                  <td style={td}>{resultText(r)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 14 }}>
        <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
          style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${HAIR}`, background: page <= 1 ? "#F3F0E9" : "#fff", color: INK, cursor: page <= 1 ? "default" : "pointer", fontSize: 13 }}>
          Prev
        </button>
        <span style={{ fontSize: 13, color: MUTED }}>Page {page} / {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${HAIR}`, background: page >= totalPages ? "#F3F0E9" : "#fff", color: INK, cursor: page >= totalPages ? "default" : "pointer", fontSize: 13 }}>
          Next
        </button>
      </div>
    </div>
  );
};

export default LiveGamesLogs;
